/**
 * _visit_time.js — a calendar visit's time, read from the schedule modal's
 * controls and said in words for the visit popup. Not a controller: the
 * underscore keeps esm/index.js from registering it.
 *
 * Times are browser-local and naive ("2026-10-05T18:30:00"); the server reads
 * them as the business's wall clock.
 *
 * An all-day visit is stored with a stand-in time (09:00 plus the duration)
 * because start_datetime and end_datetime are NOT NULL. That time means
 * nothing: describeWhen never shows it, and nothing should.
 */

const ALL_DAY_STAND_IN = '09:00';

/** "2026-10-05T18:30:00" from a Date, in the browser's local time. */
export function formatLocalDatetime(date) {
    const pad = (n) => n.toString().padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
        `T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

/** The date numDays working days on from startDate, counting startDate as day one. */
export function addWorkingDays(startDate, numDays) {
    const result = new Date(startDate);
    let added = 0;
    while (added < numDays - 1) {
        result.setDate(result.getDate() + 1);
        const dow = result.getDay(); // 0=Sun, 6=Sat
        if (dow !== 0 && dow !== 6) {
            added++;
        }
    }
    return result;
}

/**
 * The visit's start, end and all-day flag, from the schedule modal's state.
 *
 * On a single day, the block drawn on the day view is what the user picked,
 * so it wins over the Start Time list: the list can fail to match the block
 * (a slot it has no entry for), and losing the block silently stores the
 * visit as all-day. A block left on another date is not this visit's.
 *
 * @param {object} state
 * @param {string} state.selectedDate     'YYYY-MM-DD'
 * @param {string} state.eventTime        'HH:MM' from the Start Time list, or '' (All Day)
 * @param {number} state.durationMinutes
 * @param {number} state.numDays
 * @param {{start: Date, end: Date}|null} state.selection  the day view's block
 * @returns {{startDatetime: string, endDatetime: string, allDay: boolean}}
 */
export function visitTiming({ selectedDate, eventTime, durationMinutes, numDays, selection }) {
    if (numDays > 1) return multiDayTiming(selectedDate, eventTime, numDays);
    if (blockOnDate(selection, selectedDate)) {
        return timed(selection.start, selection.end);
    }
    if (eventTime) return forDuration(`${selectedDate}T${eventTime}:00`, durationMinutes, false);
    return forDuration(`${selectedDate}T${ALL_DAY_STAND_IN}:00`, durationMinutes, true);
}

/** "Mon, 5 Oct, 6:30 pm - 7:00 pm", or "Mon, 5 Oct, all day". */
export function describeWhen(start, end, allDay) {
    if (allDay) return `${allDayRange(start, end)}, all day`;
    let text = start.toLocaleDateString('en-AU',
        { weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });
    if (end) text += ' - ' + end.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' });
    return text;
}

function blockOnDate(selection, dateStr) {
    return !!(selection && selection.start && selection.end) &&
        formatLocalDatetime(selection.start).startsWith(dateStr);
}

function timed(start, end) {
    return { startDatetime: formatLocalDatetime(start), endDatetime: formatLocalDatetime(end), allDay: false };
}

function forDuration(startDatetime, durationMinutes, allDay) {
    const end = new Date(new Date(startDatetime).getTime() + durationMinutes * 60000);
    return { startDatetime, endDatetime: formatLocalDatetime(end), allDay };
}

/** A timed run ends at 17:00 on its last working day; an all-day run at the next midnight. */
function multiDayTiming(selectedDate, eventTime, numDays) {
    const startDatetime = `${selectedDate}T${eventTime || '00:00'}:00`;
    const end = addWorkingDays(new Date(startDatetime), numDays);
    if (eventTime) {
        end.setHours(17, 0, 0, 0);
    } else {
        end.setDate(end.getDate() + 1);
        end.setHours(0, 0, 0, 0);
    }
    return { startDatetime, endDatetime: formatLocalDatetime(end), allDay: !eventTime };
}

/** One day, or first to last: an all-day end is exclusive (the next midnight). */
function allDayRange(start, end) {
    const day = (d) => d.toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' });
    const last = end ? new Date(end.getTime() - 1) : start;
    return last.toDateString() === start.toDateString() ? day(start) : `${day(start)} - ${day(last)}`;
}
